import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from "react-router-dom";
import { Toaster } from "sonner";
import { SyncPanel } from "@/components/offline/SyncPanel";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { safeNext } from "@/pages/AuthPage";
import AuthPage from "@/pages/AuthPage";
import UniversesPage from "@/pages/UniversesPage";
import UniverseLayout from "@/pages/UniverseLayout";
import NotesPage from "@/pages/NotesPage";
import LorePage from "@/pages/LorePage";
import EntityPage from "@/pages/EntityPage";
import MapsPage from "@/pages/MapsPage";
import TimelinesPage from "@/pages/TimelinesPage";
import TeamPage from "@/pages/TeamPage";
import RelationshipsPage from "@/pages/RelationshipsPage";
import QuotesPage from "@/pages/QuotesPage";
import ActivityPage from "@/pages/ActivityPage";
import StudioPage from "@/pages/StudioPage";
import LibraryPage from "@/pages/LibraryPage";
import ReaderPage from "@/pages/ReaderPage";
import SharedStoryPage from "@/pages/SharedStoryPage";
import StoryPage from "@/pages/StoryPage";
import ChapterPage from "@/pages/ChapterPage";
import ComparePage from "@/pages/ComparePage";
import AskPage from "@/pages/AskPage";
import LanguagesPage from "@/pages/LanguagesPage";
import LanguagePage from "@/pages/LanguagePage";
import SettingsPage from "@/pages/SettingsPage";
import PricingPage from "@/pages/PricingPage";
import PaymentResultPage from "@/pages/PaymentResultPage";
import AdminPage from "@/pages/AdminPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import UpdatesPage from "@/pages/UpdatesPage";
import UnsubscribePage from "@/pages/UnsubscribePage";
import LandingPage from "@/pages/LandingPage";
import LicensePage from "@/pages/LicensePage";
import AccountPage from "@/pages/AccountPage";
import SecurityDenyPage from "@/pages/SecurityDenyPage";
import StatsPage from "@/pages/StatsPage";
import InspirationPage from "@/pages/InspirationPage";
import RecipesPage from "@/pages/RecipesPage";
import CookbookPage from "@/pages/CookbookPage";
import { MusicProvider } from "@/lib/music/MusicProvider";
import { MiniBar } from "@/components/music/MiniBar";
import { RewardWatcher } from "@/components/invite/RewardWatcher";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="min-h-screen flex items-center justify-center text-[#A8A090] font-display text-2xl" data-testid="auth-loading">
        Opening the archive…
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  if (user.email_verified === false) return <VerifyEmailPage />;
  return <>{children}<RewardWatcher /></>;
}

function Home() {
  const { user } = useAuth();
  if (user === false) return <LandingPage />;
  return <Protected><UniversesPage /></Protected>;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  const [params] = useSearchParams();
  if (user) return <Navigate to={safeNext(params.get("next"))} replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <MusicProvider>
      <BrowserRouter>
        <MiniBar />
        <Routes>
          <Route path="/login" element={<PublicOnly><AuthPage mode="login" /></PublicOnly>} />
          <Route path="/register" element={<PublicOnly><AuthPage mode="register" /></PublicOnly>} />
          <Route path="/forgot-password" element={<PublicOnly><ForgotPasswordPage /></PublicOnly>} />
          <Route path="/unsubscribe" element={<UnsubscribePage />} />
          <Route path="/updates" element={<UpdatesPage />} />
          <Route path="/" element={<Home />} />
          <Route path="/license" element={<LicensePage />} />
          <Route path="/security/deny" element={<SecurityDenyPage />} />
          <Route path="/account" element={<Protected><AccountPage /></Protected>} />
          <Route path="/pricing" element={<Protected><PricingPage /></Protected>} />
          <Route path="/library" element={<Protected><LibraryPage /></Protected>} />
          <Route path="/stats" element={<Protected><StatsPage /></Protected>} />
          <Route path="/inspiration" element={<Protected><InspirationPage /></Protected>} />
          <Route path="/cookbook" element={<Protected><CookbookPage /></Protected>} />
          <Route path="/cookbook/:rid" element={<Protected><CookbookPage /></Protected>} />
          <Route path="/s/:sid" element={<SharedStoryPage />} />
          <Route path="/read/:sid" element={<Protected><ReaderPage /></Protected>} />
          <Route path="/payment/success" element={<Protected><PaymentResultPage /></Protected>} />
          <Route path="/payment/cancel" element={<Protected><PaymentResultPage cancelled /></Protected>} />
          <Route path="/admin" element={<Protected><AdminPage /></Protected>} />
          <Route path="/u/:uid" element={<Protected><UniverseLayout /></Protected>}>
            <Route index element={<Navigate to="notes" replace />} />
            <Route path="notes" element={<NotesPage />} />
            <Route path="lore" element={<LorePage />} />
            <Route path="lore/:eid" element={<EntityPage />} />
            <Route path="maps" element={<MapsPage />} />
            <Route path="maps/:mid" element={<MapsPage />} />
            <Route path="timelines" element={<TimelinesPage />} />
            <Route path="languages" element={<LanguagesPage />} />
            <Route path="languages/:lid" element={<LanguagePage />} />
            <Route path="team" element={<TeamPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="ask" element={<AskPage />} />
            <Route path="quotes" element={<QuotesPage />} />
            <Route path="recipes" element={<RecipesPage />} />
            <Route path="recipes/:rid" element={<RecipesPage />} />
            <Route path="relationships" element={<RelationshipsPage />} />
            <Route path="activity" element={<ActivityPage />} />
            <Route path="studio" element={<StudioPage />} />
            <Route path="studio/:sid" element={<StoryPage />} />
            <Route path="studio/:sid/compare" element={<ComparePage />} />
            <Route path="studio/:sid/c/:cid" element={<ChapterPage />} />
          </Route>
          <Route path="/p/:uid" element={<UniverseLayout isPublic />}>
            <Route index element={<Navigate to="lore" replace />} />
            <Route path="lore" element={<LorePage />} />
            <Route path="lore/:eid" element={<EntityPage />} />
            <Route path="maps" element={<MapsPage />} />
            <Route path="maps/:mid" element={<MapsPage />} />
            <Route path="timelines" element={<TimelinesPage />} />
            <Route path="languages" element={<LanguagesPage />} />
            <Route path="languages/:lid" element={<LanguagePage />} />
            <Route path="quotes" element={<QuotesPage />} />
            <Route path="relationships" element={<RelationshipsPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      </MusicProvider>
      <SyncPanel />
      <Toaster theme="dark" position="bottom-right" offset={120} toastOptions={{ style: { background: "#13171F", border: "1px solid #2D3442", color: "#F3EBDD" } }} />
    </AuthProvider>
  );
}

export default App;
